import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit3, RefreshCw, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { WarehouseTable, useInlineEdit, createColumnFactory } from '../../../../shared/table';
import type { ColumnDefinition, TableAction, ServerFetchParams } from '../../../../shared/table/types/table';
import { itemBinSizeService } from '../../../../../services/itemBinSizeService';
import { ItemBinSizeDto, CreateItemBinSizeRequest, ItemBinSizePagedQueryParams } from '../../../../../types/itemBinSize';
import { stcBinSizeService } from '../../../../../services/stcBinSizeService';
import { itemBinSizeGroupService } from '../../../../../services/itemBinSizeGroupService';
import { StcBinSizeDto } from '../../../../../types/stcBinSize';
import { ItemBinSizeGroupDto } from '../../../../../types/itemBinSizeGroup';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface SelectedRows {
    [key: string]: boolean;
}

interface ItemBinSizeDataTableProps {
    selectedRows: SelectedRows;
    onRowSelection: (rowId: string, selected: boolean) => void;
    onBulkSelection: (rows: SelectedRows) => void;
    onDataChange: (data: ItemBinSizeDto[]) => void;
    onOpenSidePanel: () => void;
    refreshTrigger?: number;
}

const CreateForm: React.FC<{
    onClose: () => void;
    onSave: (request: CreateItemBinSizeRequest) => void;
    groups: ItemBinSizeGroupDto[];
    companyNo: string;
}> = ({ onClose, onSave, groups, companyNo }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState<'direct' | 'group'>('direct');
    const [showInfo, setShowInfo] = useState(false);
    const [formData, setFormData] = useState<CreateItemBinSizeRequest>({
        companyNo: companyNo,
        itemNo: '',
        binSizeNo: 1,
        maxQuantity: 0,
        putAwayPriorityNo: 100
    });
    const [binSizes, setBinSizes] = useState<StcBinSizeDto[]>([]);
    const [loadingLookups, setLoadingLookups] = useState<boolean>(false);

    useEffect(() => {
        const loadLookupData = async () => {
            try {
                setLoadingLookups(true);
                const binSizesData = await stcBinSizeService.getCurrent();
                setBinSizes(binSizesData);
            } catch (err: any) {
                console.error('Feil ved lasting av oppslagsdata:', err);
            } finally {
                setLoadingLookups(false);
            }
        };
        loadLookupData();
    }, []);

    const handleModeChange = (newMode: 'direct' | 'group') => {
        setMode(newMode);
        if (newMode === 'group') {
            setFormData(prev => ({ ...prev, itemNo: undefined, itemBinSizeGroupID: groups[0]?.itemBinSizeGroupID }));
        } else {
            setFormData(prev => ({ ...prev, itemNo: '', itemBinSizeGroupID: undefined }));
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
            <h4 className="text-sm font-medium text-gray-900 mb-3">{t('manager.modules.adminItemsLocations.binSize.createTitle', {}, 'Opprett ny vare-lokasjonsstørrelse')}</h4>

            {/* Uniqueness info toggle-bar */}
            <button
                type="button"
                onClick={() => setShowInfo(prev => !prev)}
                className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 mb-2"
            >
                <Info className="w-3.5 h-3.5" />
                <span>{t('manager.modules.adminItemsLocations.binSize.uniquenessToggle', {}, 'Krav til unikhet')}</span>
                {showInfo ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showInfo && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3 text-xs text-blue-800 space-y-1">
                    <p className="font-medium">{t('manager.modules.adminItemsLocations.binSize.uniquenessIntro', {}, 'Kombinasjonen av disse feltene må være unik:')}</p>
                    <ul className="list-disc ml-4 space-y-0.5">
                        <li><strong>{t('manager.modules.adminItemsLocations.binSize.directLabel', {}, 'Direkte:')}</strong> {t('manager.modules.adminItemsLocations.binSize.directUniqueness', {}, 'Selskap + Varenr. + Lokasjonsstørrelse (kun én rad per vare per størrelse)')}</li>
                        <li><strong>{t('manager.modules.adminItemsLocations.binSize.groupLabel', {}, 'Gruppemal:')}</strong> {t('manager.modules.adminItemsLocations.binSize.groupUniqueness', {}, 'Selskap + Størrelsesgruppe + Lokasjonsstørrelse (kun én mal per gruppe per størrelse)')}</li>
                    </ul>
                    <p className="text-blue-600 mt-1">{t('manager.modules.adminItemsLocations.binSize.conflictWarning', {}, 'Ved konflikt vil opprettelsen feile med en feilmelding.')}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
                {/* Mode selector */}
                <div className="flex items-center gap-4 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name="createMode"
                            checked={mode === 'direct'}
                            onChange={() => handleModeChange('direct')}
                            className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm text-gray-700">{t('manager.modules.adminItemsLocations.binSize.modeDirect', {}, 'Direkte (per vare)')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name="createMode"
                            checked={mode === 'group'}
                            onChange={() => handleModeChange('group')}
                            className="w-4 h-4 text-blue-600"
                            disabled={groups.length === 0}
                        />
                        <span className={`text-sm ${groups.length === 0 ? 'text-gray-400' : 'text-gray-700'}`}>
                            {t('manager.modules.adminItemsLocations.binSize.modeGroup', {}, 'Gruppemal (per størrelsesgruppe)')}
                        </span>
                    </label>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminItemsLocations.binSize.companyLabel', {}, 'Selskap')}</label>
                        <input
                            type="text"
                            value={formData.companyNo}
                            onChange={(e) => setFormData(prev => ({ ...prev, companyNo: e.target.value }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                            required
                        />
                    </div>

                    {mode === 'direct' ? (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminItemsLocations.binSize.itemNoLabel', {}, 'Varenr. (obligatorisk)')}</label>
                            <input
                                type="text"
                                value={formData.itemNo || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, itemNo: e.target.value }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                                placeholder={t('manager.modules.adminItemsLocations.binSize.itemNoPlaceholder', {}, 'Skriv varenummer...')}
                            />
                        </div>
                    ) : (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminItemsLocations.binSize.groupRequiredLabel', {}, 'Størrelsesgruppe (obligatorisk)')}</label>
                            <select
                                value={formData.itemBinSizeGroupID || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, itemBinSizeGroupID: parseInt(e.target.value) || undefined }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                            >
                                <option value="">{t('manager.modules.adminItemsLocations.binSize.selectGroupOption', {}, 'Velg gruppe...')}</option>
                                {groups.map(g => (
                                    <option key={g.itemBinSizeGroupID} value={g.itemBinSizeGroupID}>
                                        {g.itemBinSizeGroupID} - {g.description1}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminItemsLocations.binSize.binSizeRequiredLabel', {}, 'Lokasjonsstørrelse (obligatorisk)')}</label>
                        <select
                            value={formData.binSizeNo}
                            onChange={(e) => setFormData(prev => ({ ...prev, binSizeNo: parseInt(e.target.value) || 1 }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                            required
                            disabled={loadingLookups}
                        >
                            <option value="">{t('manager.modules.adminItemsLocations.binSize.selectBinSizeOption', {}, 'Velg størrelse...')}</option>
                            {binSizes.map(size => (
                                <option key={size.binSizeNo} value={size.binSizeNo}>
                                    {size.binSizeNo} - {size.description1}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminItemsLocations.binSize.putAwayPriorityLabel', {}, 'Innlegg.prioritet')}</label>
                        <input
                            type="number"
                            value={formData.putAwayPriorityNo}
                            onChange={(e) => setFormData(prev => ({ ...prev, putAwayPriorityNo: parseInt(e.target.value) || 100 }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                            required
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('manager.modules.adminItemsLocations.binSize.maxQuantityLabel', {}, 'Maks antall')}</label>
                        <input
                            type="number"
                            value={formData.maxQuantity}
                            onChange={(e) => setFormData(prev => ({ ...prev, maxQuantity: parseInt(e.target.value) || 0 }))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                            required
                        />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="submit"
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        disabled={loadingLookups}
                    >
                        {t('common.buttons.create', {}, 'Opprett')}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                        {t('common.buttons.cancel', {}, 'Avbryt')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export const ItemBinSizeDataTable: React.FC<ItemBinSizeDataTableProps> = ({
    selectedRows,
    onRowSelection,
    onBulkSelection,
    onDataChange,
    onOpenSidePanel,
    refreshTrigger
}) => {
    const [serverItems, setServerItems] = useState<ItemBinSizeDto[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [groups, setGroups] = useState<ItemBinSizeGroupDto[]>([]);
    const [binSizes, setBinSizes] = useState<StcBinSizeDto[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const { user } = useAuth();
    const { t } = useTranslation();
    const latestFetchParamsRef = useRef<ItemBinSizePagedQueryParams>({ pageNumber: 1, pageSize: 50 });
    const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prevFiltersRef = useRef<Record<string, string>>({});
    const prevDropdownRef = useRef<Record<string, string[]>>({});
    // B1 (softpost): skille mellom første last og senere fetch (full spinner kun ved oppstart)
    const initialLoadDoneRef = useRef(false);
    // Monotonisk teller — forkaster utdaterte svar (siste fetch vinner) i stedet for å droppe
    // overlappende fetch. Viktig fordi dropdown-valg fyrer umiddelbart (rask klikking).
    const fetchIdRef = useRef(0);

    const columnFactory = createColumnFactory<ItemBinSizeDto>();

    // Load groups + bin sizes for dropdowns (inline edit + B3 flervalgs lookup-filter)
    useEffect(() => {
        const loadLookups = async () => {
            try {
                const groupsData = await itemBinSizeGroupService.getCurrent();
                setGroups(groupsData);
            } catch (err) {
                console.warn('Kunne ikke laste størrelsesgrupper:', err);
                setGroups([]);
            }
            try {
                const binSizesData = await stcBinSizeService.getCurrent();
                setBinSizes(binSizesData);
            } catch (err) {
                console.warn('Kunne ikke laste lokasjonsstørrelser:', err);
                setBinSizes([]);
            }
        };
        loadLookups();
    }, []);

    // B3: valglister for flervalgs lookup-filter (kombinert syntetisk datasett —
    // ColumnDropdownFilter leser item[column.key] per kolonne, og hopper over null/undefined).
    // binSize-rader bærer binSizeNo (+ beskrivelse), gruppe-rader bærer itemBinSizeGroupID (+ beskrivelse).
    const dropdownOptionsData = useMemo<ItemBinSizeDto[]>(() => {
        const binSizeRows = binSizes.map(bs => ({
            id: -1, companyNo: '', itemNo: '',
            binSizeNo: bs.binSizeNo, binSizeDescription: bs.description1,
            maxQuantity: 0, putAwayPriorityNo: 0,
            itemBinSizeGroupID: undefined, groupDescription: undefined,
        } as ItemBinSizeDto));
        const groupRows = groups.map(g => ({
            id: -1, companyNo: '', itemNo: '',
            binSizeNo: undefined as unknown as number, binSizeDescription: undefined,
            maxQuantity: 0, putAwayPriorityNo: 0,
            itemBinSizeGroupID: g.itemBinSizeGroupID, groupDescription: g.description1,
        } as ItemBinSizeDto));
        return [...binSizeRows, ...groupRows];
    }, [binSizes, groups]);

    // Build group options for inline edit dropdown
    const groupOptions = useMemo(() => [
        { value: '', label: t('manager.modules.adminItemsLocations.binSize.noGroupOption', {}, 'Ingen gruppe') },
        ...groups.map(g => ({
            value: String(g.itemBinSizeGroupID),
            label: `${g.itemBinSizeGroupID} - ${g.description1}`
        }))
    ], [groups, t]);

    // Fetch paged data
    const fetchPagedItems = useCallback(async (params: ItemBinSizePagedQueryParams) => {
        // B1 (softpost): vis full spinner kun ved første last — senere fetch (filter/sort/side)
        // lar tabellen bli stående montert slik at fokus i filterfeltet beholdes (ingen "blafring")
        if (!initialLoadDoneRef.current) {
            setIsLoading(true);
        }
        setError('');

        // Tildel et request-ID; hvis en nyere fetch starter før denne er ferdig, forkastes
        // dette svaret slik at siste filter alltid vinner (ingen droppede valg ved rask klikking)
        const thisRequestId = ++fetchIdRef.current;
        try {
            const result = await itemBinSizeService.getPaged(params);
            if (thisRequestId !== fetchIdRef.current) return; // utdatert svar — forkast
            setServerItems(result.items);
            setTotalRecords(result.totalRecords);
            onDataChange(result.items);
            latestFetchParamsRef.current = params;
            initialLoadDoneRef.current = true;
        } catch (err: any) {
            if (thisRequestId === fetchIdRef.current) {
                setError(err.message || t('manager.modules.adminItemsLocations.binSize.errorLoad', {}, 'Feil ved lasting av data'));
            }
        } finally {
            if (thisRequestId === fetchIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [onDataChange, t]);

    // Initial load
    useEffect(() => {
        fetchPagedItems({ pageNumber: 1, pageSize: 50 });
    }, [fetchPagedItems]);

    // Refresh trigger from parent
    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            fetchPagedItems(latestFetchParamsRef.current);
        }
    }, [refreshTrigger, fetchPagedItems]);

    const refreshData = useCallback(() => {
        fetchPagedItems(latestFetchParamsRef.current);
    }, [fetchPagedItems]);

    // Server fetch handler — maps ServerFetchParams to our API query params
    const handleServerFetch = useCallback((params: ServerFetchParams) => {
        const f = params.filters || {};
        const d = params.dropdownFilters || {};

        // B3: flervalgs lookup-filter — dropdown sender koder komma-separert (IN), og
        // overstyrer fritekst for samme kolonne. Kolonnenøkler er kode-feltene (binSizeNo, itemBinSizeGroupID).
        const binSizeNos = d.binSizeNo && d.binSizeNo.length > 0 ? d.binSizeNo.join(',') : undefined;
        const groupIDs = d.itemBinSizeGroupID && d.itemBinSizeGroupID.length > 0 ? d.itemBinSizeGroupID.join(',') : undefined;

        const queryParams: ItemBinSizePagedQueryParams = {
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
            sortColumn: params.sortColumn,
            sortDirection: params.sortDirection,
            itemNo: f.itemNo || undefined,
            itemDescription: f.itemDescription || undefined,
            binSizeNo: binSizeNos ? undefined : (f.binSizeNo || undefined),
            binSizeNos,
            maxQuantity: f.maxQuantity || undefined,
            putAwayPriorityNo: f.putAwayPriorityNo || undefined,
            groupDescription: groupIDs ? undefined : (f.itemBinSizeGroupID || undefined),
            groupIDs,
        };

        if (filterDebounceRef.current) {
            clearTimeout(filterDebounceRef.current);
        }

        // Tekstfilter (skriving) debounces; dropdown-valg (diskret) kjøres umiddelbart
        const textKeys = ['itemNo', 'itemDescription', 'binSizeNo', 'maxQuantity', 'putAwayPriorityNo', 'itemBinSizeGroupID'];
        const isTextChange = textKeys.some(key => (f[key] || '') !== (prevFiltersRef.current[key] || ''));
        const dropdownKeys = ['binSizeNo', 'itemBinSizeGroupID'];
        const isDropdownChange = dropdownKeys.some(
            key => (d[key] || []).join(',') !== (prevDropdownRef.current[key] || []).join(',')
        );
        prevFiltersRef.current = { ...f };
        prevDropdownRef.current = { ...d };

        if (isTextChange && !isDropdownChange && params.pageNumber === 1) {
            filterDebounceRef.current = setTimeout(() => {
                fetchPagedItems(queryParams);
            }, 300);
        } else {
            fetchPagedItems(queryParams);
        }
    }, [fetchPagedItems]);

    // Inline editing
    const inlineEditConfig = useInlineEdit<ItemBinSizeDto>({
        getRowId: (row) => String(row.id),
        onSave: async (_originalRow, editedData) => {
            await itemBinSizeService.update(_originalRow.id, {
                maxQuantity: editedData.maxQuantity ?? _originalRow.maxQuantity,
                putAwayPriorityNo: editedData.putAwayPriorityNo ?? _originalRow.putAwayPriorityNo,
                itemBinSizeGroupID: editedData.itemBinSizeGroupID !== undefined
                    ? (editedData.itemBinSizeGroupID || null)
                    : (_originalRow.itemBinSizeGroupID || null)
            });
        },
        onSaveSuccess: () => refreshData(),
        onSaveError: (err) => setError(err.message || t('manager.modules.adminItemsLocations.binSize.errorSave', {}, 'Feil ved lagring'))
    });

    // Column definitions
    const columns: ColumnDefinition<ItemBinSizeDto>[] = useMemo(() => [
        columnFactory.createTextColumn('itemNo', 'itemNo', t('manager.modules.adminItemsLocations.binSize.colItemNo', {}, 'Varenr.'), {
            minWidth: 130,
            sortable: true,
            filterType: 'text',
            formatter: (value: any, item: ItemBinSizeDto) => {
                if (!value && item.itemBinSizeGroupID) {
                    return t('manager.modules.adminItemsLocations.binSize.groupTemplateTag', {}, '(Gruppemal)');
                }
                return String(value || '');
            }
        }),
        columnFactory.createTextColumn('itemDescription', 'itemDescription', t('manager.modules.adminItemsLocations.binSize.colItemDescription', {}, 'Varebeskrivelse'), {
            minWidth: 200,
            sortable: true,
            filterType: 'text'
        }),
        // B3 lookup (StcBinSize): nøkkel = kode (binSizeNo) → dropdown filtrerer på kode, viser «kode - tekst».
        // id beholdes ('binSizeDescription') for å bevare lagret kolonneoppsett.
        columnFactory.createTextColumn('binSizeDescription', 'binSizeNo', t('manager.modules.adminItemsLocations.binSize.colBinSize', {}, 'Lokasjonsstørrelse'), {
            minWidth: 160,
            sortable: true,
            filterType: 'both',
            formatter: (_value: any, item: ItemBinSizeDto) =>
                item.binSizeNo ? `${item.binSizeNo} - ${item.binSizeDescription || ''}` : String(item.binSizeNo || '')
        }),
        columnFactory.createNumberColumn('putAwayPriorityNo', 'putAwayPriorityNo', t('manager.modules.adminItemsLocations.binSize.putAwayPriorityLabel', {}, 'Innlegg.prioritet'), {
            minWidth: 130,
            sortable: true,
            filterType: 'text',
            editable: true
        }),
        columnFactory.createNumberColumn('maxQuantity', 'maxQuantity', t('manager.modules.adminItemsLocations.binSize.maxQuantityLabel', {}, 'Maks antall'), {
            minWidth: 120,
            sortable: true,
            filterType: 'text',
            editable: true
        }),
        // B3 lookup (ItemBinSizeGroup): nøkkel = kode (itemBinSizeGroupID) → dropdown filtrerer på kode.
        columnFactory.createNumberColumn('itemBinSizeGroupID', 'itemBinSizeGroupID', t('manager.modules.adminItemsLocations.binSize.colSizeGroup', {}, 'Størrelsegruppe'), {
            minWidth: 180,
            sortable: true,
            filterType: 'both',
            formatter: (_value: any, item: ItemBinSizeDto) =>
                item.itemBinSizeGroupID
                    ? `${item.itemBinSizeGroupID} - ${item.groupDescription || ''}`
                    : '',
            editable: true,
            editType: 'select',
            editOptions: groupOptions
        }),
        columnFactory.createDateColumn('created', 'created', t('manager.modules.adminItemsLocations.binSize.colCreated', {}, 'Opprettet'), {
            minWidth: 140,
            sortable: true
        })
    ], [columnFactory, groupOptions, t]);

    // Selection handling — bridge to parent's SelectedRows format
    const handleSelectionChange = useCallback((ids: (string | number)[], _selectedData: ItemBinSizeDto[]) => {
        const newIds = ids.map(id => String(id));
        setSelectedIds(newIds);

        // Sync to parent's SelectedRows format
        const newSelection: SelectedRows = {};
        newIds.forEach(id => { newSelection[id] = true; });
        onBulkSelection(newSelection);
    }, [onBulkSelection]);

    // Table actions
    const tableActions: TableAction<ItemBinSizeDto>[] = useMemo(() => [
        {
            id: 'create',
            label: t('manager.modules.adminItemsLocations.binSize.actionNew', {}, 'Ny'),
            icon: Plus,
            onClick: () => setShowCreateForm(prev => !prev),
            variant: 'primary' as const
        },
        {
            id: 'bulk-edit',
            label: t('manager.modules.adminItemsLocations.binSize.actionEditCount', { '0': selectedIds.length }, `Rediger (${selectedIds.length})`),
            icon: Edit3,
            onClick: () => onOpenSidePanel(),
            disabled: () => selectedIds.length === 0,
            variant: 'secondary' as const
        },
        {
            id: 'refresh',
            label: t('common.buttons.refresh', {}, 'Oppdater'),
            icon: RefreshCw,
            onClick: () => refreshData(),
            variant: 'secondary' as const
        }
    ], [selectedIds.length, onOpenSidePanel, refreshData, t]);

    // Create handler
    const handleCreate = async (request: CreateItemBinSizeRequest) => {
        try {
            setIsLoading(true);
            await itemBinSizeService.create(request);
            setShowCreateForm(false);
            refreshData();
        } catch (err: any) {
            setError(err.message || t('manager.modules.adminItemsLocations.binSize.errorCreate', {}, 'Feil ved opprettelse'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            {showCreateForm && (
                <CreateForm
                    onClose={() => setShowCreateForm(false)}
                    onSave={handleCreate}
                    groups={groups}
                    companyNo={user?.companyNo || '001'}
                />
            )}

            <WarehouseTable
                data={serverItems}
                columns={columns}
                config={{
                    showPagination: true,
                    showColumnConfig: true,
                    showGlobalSearch: false,
                    showFilters: true,
                    showExport: false,
                    showPrint: false,
                    showSummary: false,
                    defaultPageSize: 50,
                    allowColumnReorder: true,
                    allowColumnResize: true,
                    stickyHeader: true,
                    compactMode: true,
                    persistenceKey: 'item-bin-size-table',
                    allowRowSelection: {
                        mode: 'multi',
                        selectAllMode: 'current-page'
                    }
                }}
                serverPagination={{
                    enabled: true,
                    totalRecords: totalRecords,
                    onFetchData: handleServerFetch
                }}
                selectedRows={selectedIds}
                onSelectionChange={handleSelectionChange}
                getRowId={(item) => String(item.id)}
                tableActions={tableActions}
                inlineEditConfig={inlineEditConfig}
                enableDropdownFilters={true}
                dropdownOptionsData={dropdownOptionsData.length > 0 ? dropdownOptionsData : undefined}
                loading={isLoading}
                error={error || undefined}
            />
        </div>
    );
};
